import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
// Import UI components from lift-ui
import { 
  Button, 
  Badge, 
  Input,
  DataTable,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
  Toastify,
  SingleSelect,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  SpinnerLoader,
} from "lift-ui";
import { FiltersPopover } from "./SessionFiltersPopover";
import type { TableColumn } from "lift-ui";
// Icons from lucide-react
import {
  Brain,
  Search,
  Plus,
  Trash2,
  Edit,
  Globe,
  User,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Calendar,
  X,
} from "lucide-react";
import { format } from "date-fns";
import {
  MasterMemoryItem,
  getMasterMemories,
  addMasterMemory,
  updateMasterMemory,
  deleteMasterMemory,
  UNIVERSAL_USER_ID,
  getUsersOrderedBySessions,
  getAllUsersWithDetails,
  AdminUserInfo,
} from "../../api";
import { useChatV2 } from "../../context/ChatV2Context";
import { useAuthContext } from "@lift-admin/utils/provider";

interface MasterMemoriesPanelProps {
  className?: string;
}

const AGENT_OPTIONS = [
  { value: "default", label: "Default Agent" },
  { value: "ga_dmg", label: "GA DMG Agent" },
  { value: "ga_affiliate", label: "GA Affiliate Agent" },
  { value: "similarweb", label: "SimilarWeb Agent" },
  { value: "pacing", label: "Pacing Agent" },
];

export const MasterMemoriesPanel: React.FC<MasterMemoriesPanelProps> = ({
  className,
}) => {
  // Memory type toggle (Universal vs Personal)
  const [memoryType, setMemoryType] = useState<"universal" | "personal">("universal");
  
  // All state declarations
  const [memories, setMemories] = useState<MasterMemoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<string[]>([]);
  const [userDetailsMap, setUserDetailsMap] = useState<Record<string, AdminUserInfo>>({});
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Filter states (arrays for multi-select)
  const [filterAgent, setFilterAgent] = useState<string[]>([]);
  const [filterUser, setFilterUser] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  // Sheet states
  const [selectedMemory, setSelectedMemory] = useState<MasterMemoryItem | null>(null);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [memoryToDelete, setMemoryToDelete] = useState<string | null>(null);
  
  // Form states
  const [newMemoryText, setNewMemoryText] = useState("");
  const [newMemoryIsUniversal, setNewMemoryIsUniversal] = useState(true);
  const [newMemoryUserId, setNewMemoryUserId] = useState("");
  const [newMemoryAgentId, setNewMemoryAgentId] = useState("default");
  const [editMemoryText, setEditMemoryText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { userMap } = useChatV2();
  const { canManage } = useAuthContext();
  const hasManageAccess = canManage("lift_ai");

  // Ref to prevent duplicate concurrent fetches
  const fetchingRef = useRef(false);
  // Ref to track last fetched memory type to prevent unnecessary re-fetches
  const lastFetchedTypeRef = useRef<string | null>(null);

  // Safe array accessors
  const safeMemories = Array.isArray(memories) ? memories : [];
  const safeAvailableUsers = Array.isArray(availableUsers) ? availableUsers : [];

  // Fetch users with details
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const userIds = await getUsersOrderedBySessions(100);
      const validUserIds = Array.isArray(userIds) ? userIds.filter((u) => u && u !== UNIVERSAL_USER_ID) : [];
      
      try {
        const allUsers = await getAllUsersWithDetails();
        const detailsMap: Record<string, AdminUserInfo> = {};
        for (const user of allUsers) {
          if (user?.id) {
            detailsMap[user.id] = user;
          }
        }
        setUserDetailsMap(detailsMap);
        setAvailableUsers(validUserIds);
      } catch (detailsErr) {
        console.warn("[fetchUsers] Failed to get user details:", detailsErr);
        setAvailableUsers(validUserIds);
      }
    } catch (err) {
      console.warn("[fetchUsers] Failed:", err);
      setAvailableUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Helper to get user display name
  const getUserDisplayName = (userId: string): string => {
    if (!userId) return "Unknown";
    if (userId === UNIVERSAL_USER_ID) return "Universal";
    const user = userDetailsMap[userId];
    if (user?.name) return user.name;
    return userMap?.[userId] || (userId.length > 12 ? `${userId.substring(0, 12)}...` : userId);
  };

  // Fetch memories based on type - fetch for ALL agents in parallel to avoid multiple sequential API calls
  const fetchMemories = useCallback(async () => {
    // Prevent duplicate concurrent fetches
    if (fetchingRef.current) {
      return;
    }
    
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const memoriesMap = new Map<string, MasterMemoryItem>();

      if (memoryType === "universal") {
        // Fetch universal memories for ALL agents in parallel
        const agentsToFetch = AGENT_OPTIONS.map(opt => opt.value);
        const fetchPromises = agentsToFetch.map(async (agentId) => {
          try {
            const universalResponse = await getMasterMemories(UNIVERSAL_USER_ID, agentId);
            const universalMemories = Array.isArray(universalResponse?.memories) ? universalResponse.memories : [];
            return universalMemories.filter((mem: MasterMemoryItem) => mem?.id && mem.user_id === UNIVERSAL_USER_ID);
          } catch (err) {
            console.error(`[fetchMemories] Universal fetch failed for agent ${agentId}:`, err);
            return [];
          }
        });

        const results = await Promise.all(fetchPromises);
        results.flat().forEach((mem) => {
          if (mem?.id) {
            memoriesMap.set(mem.id, mem);
          }
        });
      } else {
        // Fetch personal memories for all available users and ALL agents in parallel
        const userIds = safeAvailableUsers.filter((userId) => userId && userId !== UNIVERSAL_USER_ID);
        const agentsToFetch = AGENT_OPTIONS.map(opt => opt.value);
        
        // Create all fetch promises
        const fetchPromises: Promise<MasterMemoryItem[]>[] = [];
        
        for (const userId of userIds) {
          for (const agentId of agentsToFetch) {
            fetchPromises.push(
              (async () => {
                try {
                  const userResponse = await getMasterMemories(userId, agentId);
                  const userMemories = Array.isArray(userResponse?.memories) ? userResponse.memories : [];
                  return userMemories.filter((mem: MasterMemoryItem) => 
                    mem?.id && mem.user_id === userId && mem.user_id !== UNIVERSAL_USER_ID
                  );
                } catch (err) {
                  console.error(`[fetchMemories] User ${userId} fetch failed for agent ${agentId}:`, err);
                  return [];
                }
              })()
            );
          }
        }

        const results = await Promise.all(fetchPromises);
        results.flat().forEach((mem) => {
          if (mem?.id) {
            memoriesMap.set(mem.id, mem);
          }
        });
      }

      const allMemories = Array.from(memoriesMap.values());
      setMemories(allMemories);
    } catch (err: any) {
      console.error("[fetchMemories] Error:", err);
      setError(err?.message || "Failed to fetch memories");
      setMemories([]);
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
      fetchingRef.current = false;
    }
  }, [memoryType, safeAvailableUsers]);

  // Fetch when component mounts or when memory type changes
  useEffect(() => {
    // Skip if already fetching or if we've already fetched this type
    if (fetchingRef.current) {
      return;
    }

    // For personal memories, wait until users are loaded
    if (memoryType === "personal" && safeAvailableUsers.length === 0) {
      return;
    }

    // Only fetch if this is a new type or initial load
    if (lastFetchedTypeRef.current === null || lastFetchedTypeRef.current !== memoryType) {
      fetchMemories();
      lastFetchedTypeRef.current = memoryType;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryType, safeAvailableUsers.length]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [memoryType, filterAgent, filterUser, searchQuery]);

  // Filtered memories
  const filteredMemories = useMemo(() => {
    let filtered = safeMemories.filter((m) => {
      if (!m) return false;
      
      // Type filter
      if (memoryType === "universal") {
        if (m.user_id !== UNIVERSAL_USER_ID) return false;
      } else {
        if (m.user_id === UNIVERSAL_USER_ID) return false;
      }
      
      // Agent filter
      if (filterAgent.length > 0 && !filterAgent.includes(m.agent_id || '')) return false;
      
      // User filter (for personal)
      if (memoryType === "personal" && filterUser.length > 0 && !filterUser.includes(m.user_id || '')) return false;
      
      // Search filter
      // Free-text search: matches memory text + agent label + user
      // display name. Mirrors the SessionsPanel / WorkflowRunsPanel
      // search semantics so the admin experience stays consistent.
      if (searchQuery?.trim()) {
        const query = searchQuery.toLowerCase();
        const agentLabel = getAgentLabel(m.agent_id || '').toLowerCase();
        const userName = m.user_id && m.user_id !== UNIVERSAL_USER_ID
          ? getUserDisplayName(m.user_id).toLowerCase()
          : '';
        const haystack = `${m.memory || ''} ${agentLabel} ${userName}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      
      return true;
    });
    
    // Sort by created_at descending
    return filtered.sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [safeMemories, memoryType, filterAgent, filterUser, searchQuery]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredMemories.slice(start, end);
  }, [filteredMemories, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredMemories.length / rowsPerPage);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (size: string) => {
    setRowsPerPage(Number(size));
    setPage(0);
  };

  // Add memory
  const handleAddMemory = async () => {
    if (!hasManageAccess) {
      Toastify("error", "Only managers can add memories. Contact your administrator for access.");
      return;
    }

    const targetUserId = newMemoryIsUniversal ? UNIVERSAL_USER_ID : newMemoryUserId;

    if (!newMemoryText?.trim()) {
      Toastify("warning", "Please enter memory text");
      return;
    }

    if (!newMemoryIsUniversal && !newMemoryUserId?.trim()) {
      Toastify("warning", "Please select a user");
      return;
    }

    setSubmitting(true);
    try {
      await addMasterMemory({
        user_id: targetUserId,
        agent_id: newMemoryAgentId,
        text: newMemoryText,
      });

      Toastify("success", "Memory added successfully");
      setShowAddSheet(false);
      setNewMemoryText("");
      setNewMemoryIsUniversal(memoryType === "universal");
      setNewMemoryUserId("");
      setNewMemoryAgentId("default");
      fetchMemories();
    } catch (err: any) {
      Toastify("error", err?.message || "Failed to add memory");
    } finally {
      setSubmitting(false);
    }
  };

  // Update memory
  const handleUpdateMemory = async () => {
    if (!hasManageAccess) {
      Toastify("error", "Only managers can edit memories. Contact your administrator for access.");
      return;
    }
    if (!selectedMemory || !editMemoryText?.trim()) return;

    setSubmitting(true);
    try {
      await updateMasterMemory(selectedMemory.id, editMemoryText);
      Toastify("success", "Memory updated successfully");
      setShowEditSheet(false);
      setEditMemoryText("");
      setSelectedMemory(null);
      fetchMemories();
    } catch (err: any) {
      Toastify("error", err?.message || "Failed to update memory");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete memory
  const handleDeleteMemory = async () => {
    if (!hasManageAccess) {
      Toastify("error", "Only managers can delete memories. Contact your administrator for access.");
      return;
    }
    if (!memoryToDelete) return;

    setSubmitting(true);
    try {
      await deleteMasterMemory(memoryToDelete);
      Toastify("success", "Memory deleted successfully");
      setShowDeleteDialog(false);
      setMemoryToDelete(null);
      fetchMemories();
    } catch (err: any) {
      Toastify("error", err?.message || "Failed to delete memory");
    } finally {
      setSubmitting(false);
    }
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A";
    try {
      return format(new Date(dateStr), "MMM dd, yyyy");
    } catch {
      return dateStr;
    }
  };

  // Get agent label
  const getAgentLabel = (agentId: string | null) => {
    if (!agentId || agentId === "null" || agentId === "default") return "Default";
    return AGENT_OPTIONS.find(opt => opt.value === agentId)?.label || agentId;
  };

  // Universal Memories columns
  const universalColumns: TableColumn<any>[] = useMemo(() => {
    const cols: TableColumn<any>[] = [
      {
        key: "memory_name",
        header: "Memory Name",
        accessor: (row: any) => row.memory || "",
        width: hasManageAccess ? "50%" : "55%",
        render: (_v: any, row: any) => {
          const memoryText = row.memory || "";
          const truncated = memoryText.length > 100 ? memoryText.substring(0, 100) + "..." : memoryText;
          return (
            <div className="font-medium max-w-md">
              <div className="truncate" title={memoryText}>
                {truncated}
              </div>
            </div>
          );
        },
      },
      {
        key: "created_at",
        header: "Created At",
        accessor: (row: any) => row.created_at,
        width: "20%",
        render: (_v: any, row: any) => {
          if (!row.created_at) return <span className="text-sm text-muted-foreground">-</span>;
          const createdDate = new Date(row.created_at);
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 text-sm">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{formatDate(row.created_at)}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{format(createdDate, "HH:mm")}</span>
              </div>
            </div>
          );
        },
      },
      {
        key: "agent",
        header: "Agent",
        accessor: (row: any) => row.agent_id || "default",
        width: "20%",
        render: (_v: any, row: any) => {
          const agentLabel = getAgentLabel(row.agent_id);
          return (
            <div className="flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-lg w-fit">
              <span className="text-xs">{agentLabel}</span>
            </div>
          );
        },
      },
    ];

    if (hasManageAccess) {
      cols.push({
        key: "actions",
        header: "Actions",
        accessor: (row: any) => row.id,
        width: "10%",
        align: "center" as const,
        render: (_v: any, row: any) => (
          <div className="flex justify-center items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMemory(row);
                setEditMemoryText(row.memory || "");
                setShowEditSheet(true);
              }}
              title="Edit Memory"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-red-50 text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                setMemoryToDelete(row.id);
                setShowDeleteDialog(true);
              }}
              title="Delete Memory"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      });
    }

    return cols;
  }, [hasManageAccess]);

  // Personal Memories columns
  const personalColumns: TableColumn<any>[] = useMemo(() => {
    const cols: TableColumn<any>[] = [
      {
        key: "memory_name",
        header: "Memory Name",
        accessor: (row: any) => row.memory || "",
        width: hasManageAccess ? "40%" : "45%",
        render: (_v: any, row: any) => {
          const memoryText = row.memory || "";
          const truncated = memoryText.length > 100 ? memoryText.substring(0, 100) + "..." : memoryText;
          return (
            <div className="font-medium max-w-md">
              <div className="truncate" title={memoryText}>
                {truncated}
              </div>
            </div>
          );
        },
      },
      {
        key: "created_by",
        header: "Created By",
        accessor: (row: any) => row.user_id,
        width: "20%",
        render: (_v: any, row: any) => {
          if (!row.user_id) return <span className="text-sm text-muted-foreground">-</span>;
          const userId = row.user_id;
          return (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {getUserDisplayName(userId)}
              </span>
            </div>
          );
        },
      },
      {
        key: "created_at",
        header: "Created At",
        accessor: (row: any) => row.created_at,
        width: "20%",
        render: (_v: any, row: any) => {
          if (!row.created_at) return <span className="text-sm text-muted-foreground">-</span>;
          const createdDate = new Date(row.created_at);
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 text-sm">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{formatDate(row.created_at)}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{format(createdDate, "HH:mm")}</span>
              </div>
            </div>
          );
        },
      },
    ];

    if (hasManageAccess) {
      cols.push({
        key: "actions",
        header: "Actions",
        accessor: (row: any) => row.id,
        width: "10%",
        align: "center" as const,
        render: (_v: any, row: any) => (
          <div className="flex justify-center items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMemory(row);
                setEditMemoryText(row.memory || "");
                setShowEditSheet(true);
              }}
              title="Edit Memory"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-red-50 text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                setMemoryToDelete(row.id);
                setShowDeleteDialog(true);
              }}
              title="Delete Memory"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      });
    }

    return cols;
  }, [getUserDisplayName, hasManageAccess]);

  // Get unique users from ALL personal memories (not filtered) so filter dropdown always shows all users
  const uniqueUsers = useMemo(() => {
    const userIds = new Set<string>();
    safeMemories.forEach((mem) => {
      if (mem.user_id && mem.user_id !== UNIVERSAL_USER_ID) {
        userIds.add(mem.user_id);
      }
    });
    return Array.from(userIds).sort((a, b) => {
      const nameA = getUserDisplayName(a);
      const nameB = getUserDisplayName(b);
      return nameA.localeCompare(nameB);
    });
  }, [safeMemories, getUserDisplayName]);

  return (
    <div className={cn("flex-1 min-h-0 overflow-hidden flex flex-col bg-white px-8 pb-6", className)}>
      {/* Toggle row — only the Universal/Personal switch lives here so the
       *  Add Memory action is always grouped with the filters below.
       *  Previously the Add button was in this row AND the layout was
       *  re-rendering inconsistently (some users saw a duplicate after HMR
       *  because of the mismatched `items-center` vs `items-end` baselines).
       */}
      <div className="flex items-center mb-4 flex-shrink-0">
        <Tabs value={memoryType} onValueChange={(val) => {
          setMemoryType(val as "universal" | "personal");
          if (val === "universal") {
            setFilterUser([]); // Reset user filter when switching to universal
          }
        }} className="w-auto">
          <TabsList>
            <TabsTrigger value="universal">Universal Memories</TabsTrigger>
            <TabsTrigger value="personal">Personal Memories</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filters + Add action — single row. Search is the first slot to
       *  match SessionsPanel and WorkflowRunsPanel; the Add Memory button
       *  is pushed to the far right via `ml-auto` so it always sits on the
       *  same baseline as the filter inputs (no center-vs-end mismatch).
       */}
      <div className="flex flex-wrap items-end gap-3 mb-3 flex-shrink-0">
        <div className="space-y-1.5 w-[220px]">
          <Label className="text-sm font-medium">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#585755] pointer-events-none" />
            <Input
              id="search-memories"
              placeholder="Search memories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-[46px] bg-white border-input hover:border-[#585755]"
              aria-label="Search memories"
            />
          </div>
        </div>

        {/* Unified Agent (+ User on personal) filter popover. The User
         *  section is only added when the panel is in "personal" mode —
         *  universal memories are not user-scoped, so a User filter
         *  there would be meaningless. */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium opacity-0 select-none">
            Filters
          </Label>
          <FiltersPopover
            sections={[
              {
                key: "agent",
                label: "Agent",
                options: AGENT_OPTIONS.map((agent) => ({
                  label: agent.label,
                  value: agent.value,
                })),
                value: filterAgent,
                onChange: setFilterAgent,
              },
              ...(memoryType === "personal"
                ? [
                    {
                      key: "user",
                      label: "User",
                      searchable: true,
                      searchPlaceholder: "Search users…",
                      options: uniqueUsers.map((userId) => ({
                        label: getUserDisplayName(userId),
                        value: userId,
                      })),
                      value: filterUser,
                      onChange: setFilterUser,
                    },
                  ]
                : []),
            ]}
          />
        </div>

        {/* Add Memory action — always rendered on the same baseline as the
         *  filter inputs and pushed right via `ml-auto`.
         *  Permission enforcement happens at submit time (handleAddMemory
         *  checks `hasManageAccess` and the backend re-validates).
         */}
        <Button
          variant="default"
          size="sm"
          onClick={() => {
            setNewMemoryIsUniversal(memoryType === "universal");
            setShowAddSheet(true);
          }}
          className={cn(
            "gap-2 bg-[#E3FF6A] hover:bg-[#DAF565] text-[#242424] border border-[#DAF565] h-[46px] ml-auto"
          )}
        >
          <Plus className="w-4 h-4" />
          Add Memory
        </Button>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <SpinnerLoader className="h-8 w-8" />
            <span className="text-sm text-muted-foreground">Loading memories...</span>
          </div>
        </div>
      )}

      {/* DataTable */}
      {!loading && (
        <DataTable
          className="flex-1 min-h-0"
          maxHeight="calc(100vh - 330px)"
          columns={memoryType === "universal" ? universalColumns : personalColumns}
          data={paginatedData}
          loading={false}
          emptyMessage="No memories found."
          stickyHeader
          stickyFooter
          showActions={false}
          pagination={{
            currentPage: page + 1,
            totalPages: Math.max(1, totalPages),
            pageSize: rowsPerPage,
            totalItems: filteredMemories.length,
            onPageChange: (p) => handlePageChange(p - 1),
            onPageSizeChange: (size) => handleRowsPerPageChange(String(size)),
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      )}

      {/* Add Memory Sheet */}
      <Sheet open={showAddSheet} onOpenChange={setShowAddSheet}>
        <SheetContent side="right" className="w-[380px] sm:w-[420px] sm:max-w-[420px] flex flex-col bg-white z-[100] !p-0">
          <SheetHeader className="px-5 pt-4 pb-0.5 flex-shrink-0">
            <SheetTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Add New Memory
            </SheetTitle>
            <SheetDescription>Add a new memory to the system.</SheetDescription>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 px-5 pt-2 pb-4">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Memory Type <span className="text-red-600">*</span></Label>
                <Tabs 
                  value={newMemoryIsUniversal ? "universal" : "specific"} 
                  onValueChange={(value) => {
                    if (value === "universal") {
                      setNewMemoryIsUniversal(true);
                      setNewMemoryUserId("");
                    } else {
                      setNewMemoryIsUniversal(false);
                    }
                  }}
                  className="w-full"
                >
                  <TabsList className="w-full">
                    <TabsTrigger value="universal" className="flex-1">
                      Universal Memories
                    </TabsTrigger>
                    <TabsTrigger value="specific" className="flex-1">
                      Personal Memories
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {!newMemoryIsUniversal && (
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Select User <span className="text-red-600">*</span></Label>
                  <SingleSelect
                    options={safeAvailableUsers.map((userId) => ({
                      label: getUserDisplayName(userId),
                      value: userId,
                    }))}
                    defaultValue={newMemoryUserId}
                    onChange={(value) => setNewMemoryUserId(value)}
                    placeholder="Select a user"
                    containerClassName="w-full"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Agent</Label>
                <SingleSelect
                  options={AGENT_OPTIONS.map((agent) => ({
                    label: agent.label,
                    value: agent.value,
                  }))}
                  defaultValue={newMemoryAgentId}
                  onChange={(value) => setNewMemoryAgentId(value)}
                  placeholder="Select agent"
                  containerClassName="w-full"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Memory Text <span className="text-red-600">*</span></Label>
                <textarea
                  placeholder="Enter the memory content..."
                  value={newMemoryText}
                  onChange={(e) => setNewMemoryText(e.target.value)}
                  rows={8}
                  className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-vertical"
                />
              </div>
            </div>
          </div>
          
          <SheetFooter className="px-5 py-3 border-t border-border flex-shrink-0">
            <div className="flex items-center justify-end gap-3 w-full">
              <Button variant="outline" onClick={() => setShowAddSheet(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={handleAddMemory}
                disabled={submitting || !newMemoryText?.trim() || (!newMemoryIsUniversal && !newMemoryUserId?.trim())}
                className="gap-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Add Memory
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Memory Sheet */}
      <Sheet open={showEditSheet} onOpenChange={setShowEditSheet}>
        <SheetContent side="right" className="w-[380px] sm:w-[420px] sm:max-w-[420px] flex flex-col bg-white z-[100] !p-0">
          <SheetHeader className="px-5 pt-1 pb-2 flex-shrink-0">
            <SheetTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Memory
            </SheetTitle>
            <SheetDescription>Update the memory content.</SheetDescription>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 px-5 pt-2 pb-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Memory Text <span className="text-red-600">*</span></Label>
              <textarea
                placeholder="Enter the memory content..."
                value={editMemoryText}
                onChange={(e) => setEditMemoryText(e.target.value)}
                rows={12}
                className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-vertical"
              />
            </div>
          </div>
          
          <SheetFooter className="px-5 py-3 border-t border-border flex-shrink-0">
            <div className="flex items-center justify-end gap-3 w-full">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditSheet(false);
                  setSelectedMemory(null);
                  setEditMemoryText("");
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateMemory} disabled={submitting || !editMemoryText?.trim()} className="gap-2">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Save Changes
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-4 w-4" />
              Delete Memory
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this memory? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleDeleteMemory} disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default MasterMemoriesPanel;
